/**
 * The LTO archive drawn: a tape library (rows of cartridges, each with its barcode label), its robot, and a drive.
 * `TapeRobot` plays the robot fetching a cartridge and loading it in the drive while the archive is searched or a tape
 * is being fetched for a restore; `TapeDrive` is the drive alone, its reels turning while a tape is read; `Cartridge`
 * is one LTO cartridge with its label, for the tapes and an item's panel. Everything moves with CSS (no timers), and
 * nothing moves for someone who asked the system for less motion.
 */
import type { CSSProperties } from 'react';

/** A cartridge's shell colour by generation (AirPost's own palette, so the generations tell apart at a glance). */
export const GENERATION_COLOURS: Record<string, string> = {
  'LTO-5': '#7d2e3a',
  'LTO-6': '#2b2f36',
  'LTO-7': '#5b3f8c',
  'LTO-8': '#24446e',
  'LTO-9': '#2f6b4f',
};
export const shellColour = (generation: string | null | undefined): string => (generation && GENERATION_COLOURS[generation]) || '#3c4658';
/** The short media code printed after the barcode on LTO labels (L5 … L9). */
export const mediaCode = (generation: string | null | undefined): string => (generation ? `L${generation.slice(4)}` : '');

const COLS = 13;
const ROWS = 3;
const SLOT_W = 34;
const SLOT_H = 15;
const X0 = 26;
const Y0 = 44;
const DX = 37;
const DY = 46;
const DRIVE = { x: 548, y: 62, w: 70, h: 96 };
/** The picker's home, in front of the drive's mouth. */
const HOME = { x: DRIVE.x - 34, y: DRIVE.y + 40 };

/** A stable, pretty slot for a barcode (the library's real slots are not known to AirPost). */
export function slotOf(barcode: string): { col: number; row: number } {
  let h = 2166136261;
  for (const ch of barcode) h = Math.imul(h ^ ch.charCodeAt(0), 16777619);
  const n = Math.abs(h) % (COLS * ROWS);
  return { col: n % COLS, row: Math.floor(n / COLS) };
}

const FILLER = ['MS0041', 'MS0102', 'NW0007', 'MS0233', 'SP0019', 'MS0350', 'NW0044', 'MS0412', 'MS0021', 'SP0090', 'MS0177', 'NW0120', 'MS0300'];

function Slot({ x, y, label, colour, hidden }: { x: number; y: number; label: string; colour: string; hidden?: boolean }) {
  return (
    <g className={`lto-slot${hidden ? ' taken' : ''}`} transform={`translate(${x} ${y})`}>
      <rect className="lto-slot-hole" x={-2} y={-2} width={SLOT_W + 4} height={SLOT_H + 4} rx={2} />
      <g className="lto-slot-cart">
        <rect width={SLOT_W} height={SLOT_H} rx={2} fill={colour} />
        <rect x={3} y={3} width={SLOT_W - 6} height={SLOT_H - 6} rx={1} fill="#f4f1e6" />
        <text x={SLOT_W / 2} y={SLOT_H / 2 + 2.3} className="lto-slot-text">
          {label}
        </text>
      </g>
    </g>
  );
}

/**
 * The robot fetching `barcode` from its slot and loading it in the drive, again and again while `active`.
 * `caption` is said under it (searching, fetching a tape for a restore).
 */
export function TapeRobot({ barcode, generation, caption, compact = false }: { barcode: string; generation?: string | null; caption?: string; compact?: boolean }) {
  const code = barcode.toUpperCase().slice(0, 12) || 'LTO';
  const { col, row } = slotOf(code);
  const sx = X0 + col * DX;
  const sy = Y0 + row * DY;
  const colour = shellColour(generation ?? 'LTO-8');
  const vars = { '--sx': `${sx - HOME.x}px`, '--sy': `${sy - HOME.y}px` } as CSSProperties;
  return (
    <div className={`lto-robot${compact ? ' compact' : ''}`} role="status" aria-live="polite">
      <svg viewBox="0 0 640 196" aria-hidden="true">
        <defs>
          <linearGradient id="lto-frame" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#1b2a44" />
            <stop offset="1" stopColor="#0e1729" />
          </linearGradient>
        </defs>
        <rect x={6} y={6} width={628} height={184} rx={10} fill="url(#lto-frame)" stroke="#2c4064" />
        <text x={22} y={28} className="lto-robot-brand">
          LTO LIBRARY · AIRPOST ARCHIVE
        </text>
        <circle cx={612} cy={24} r={4} className="lto-led" />
        {Array.from({ length: ROWS }, (_, r) =>
          Array.from({ length: COLS }, (_, c) => {
            const mine = r === row && c === col;
            const label = mine ? code : FILLER[(r * 5 + c * 3) % FILLER.length]!;
            return <Slot key={`${r}-${c}`} x={X0 + c * DX} y={Y0 + r * DY} label={label.slice(0, 6)} colour={mine ? colour : shellColour(['LTO-6', 'LTO-7', 'LTO-8', 'LTO-8', 'LTO-9'][(r + c) % 5])} hidden={mine} />;
          }),
        )}
        {/* the rails the picker runs on */}
        <line x1={18} y1={Y0 - 10} x2={DRIVE.x - 6} y2={Y0 - 10} className="lto-rail" />
        <line x1={18} y1={Y0 + ROWS * DY - 12} x2={DRIVE.x - 6} y2={Y0 + ROWS * DY - 12} className="lto-rail" />
        {/* the drive */}
        <g transform={`translate(${DRIVE.x} ${DRIVE.y})`} className="lto-drive-box">
          <rect width={DRIVE.w} height={DRIVE.h} rx={6} />
          <rect x={6} y={34} width={DRIVE.w - 12} height={20} rx={2} className="lto-drive-mouth" />
          <circle cx={22} cy={74} r={9} className="lto-reel" />
          <circle cx={48} cy={74} r={9} className="lto-reel" />
          <circle cx={DRIVE.w - 10} cy={10} r={3} className="lto-drive-led" />
          <text x={DRIVE.w / 2} y={22} className="lto-drive-text">
            LTO DRIVE
          </text>
        </g>
        {/* the picker, carrying the cartridge from its slot to the drive */}
        <g className="lto-picker" style={vars} transform={`translate(${HOME.x} ${HOME.y})`}>
          <g className="lto-picker-move">
            <rect x={-6} y={-12} width={SLOT_W + 12} height={SLOT_H + 24} rx={4} className="lto-picker-body" />
            <g className="lto-carried">
              <rect width={SLOT_W} height={SLOT_H} rx={2} fill={colour} />
              <rect x={3} y={3} width={SLOT_W - 6} height={SLOT_H - 6} rx={1} fill="#f4f1e6" />
              <text x={SLOT_W / 2} y={SLOT_H / 2 + 2.3} className="lto-slot-text">
                {code.slice(0, 6)}
              </text>
            </g>
            <rect x={-6} y={-12} width={SLOT_W + 12} height={5} rx={2} className="lto-picker-head" />
          </g>
        </g>
      </svg>
      {caption && (
        <div className="lto-robot-caption">
          <span className="lto-robot-dot" />
          {caption} <b className="lto-barcode-chip">{code}</b>
        </div>
      )}
    </div>
  );
}

/** The drive with a tape in it, its reels turning while `reading`. */
export function TapeDrive({ reading, barcode }: { reading: boolean; barcode: string }) {
  return (
    <span className={`lto-mini-drive${reading ? ' reading' : ''}`} title={barcode} aria-hidden="true">
      <svg viewBox="0 0 64 34">
        <rect x={1} y={1} width={62} height={32} rx={4} className="lto-mini-body" />
        <g className="lto-mini-reel" style={{ transformOrigin: '20px 17px' }}>
          <circle cx={20} cy={17} r={9} />
          <path d="M20 9 V25 M12 17 H28" />
        </g>
        <g className="lto-mini-reel" style={{ transformOrigin: '44px 17px' }}>
          <circle cx={44} cy={17} r={9} />
          <path d="M44 9 V25 M36 17 H52" />
        </g>
        <path d="M20 26 Q32 32 44 26" className="lto-mini-tape" />
      </svg>
    </span>
  );
}

/** The drive's front with the cartridge in its slot: its label, the reels turning and the activity light while it reads, how far. */
export function TapeDriveFront({ barcode, generation, reading, progress }: { barcode: string; generation?: string | null; reading: boolean; progress: number }) {
  const colour = shellColour(generation ?? 'LTO-8');
  return (
    <div className={`lto-front${reading ? ' reading' : ''}`} aria-hidden="true">
      <svg viewBox="0 0 420 150">
        <defs>
          <linearGradient id="lto-metal" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#2a3a58" />
            <stop offset="0.5" stopColor="#1b2840" />
            <stop offset="1" stopColor="#121c30" />
          </linearGradient>
        </defs>
        <rect x={6} y={28} width={408} height={112} rx={10} fill="url(#lto-metal)" stroke="#3b5688" />
        <text x={24} y={56} className="lto-front-brand">
          LTO ULTRIUM
        </text>
        <text x={24} y={72} className="lto-front-gen">
          {generation ?? 'LTO'} · TAPE DRIVE
        </text>
        {/* the slot, and the cartridge in it with its label */}
        <rect x={140} y={52} width={180} height={30} rx={4} className="lto-front-slot" />
        <g transform="translate(152 14)">
          <rect width={156} height={52} rx={4} fill={colour} stroke="rgba(255,255,255,0.18)" />
          <rect x={10} y={8} width={136} height={24} rx={2} fill="#f4f1e6" />
          <text x={78} y={25} className="lto-front-label">
            {barcode.toUpperCase().slice(0, 12)}
          </text>
        </g>
        <rect x={140} y={66} width={180} height={16} rx={3} fill="#05080f" />
        {/* the reels behind their window */}
        <rect x={24} y={86} width={92} height={42} rx={6} className="lto-front-window" />
        <g className="lto-front-reel" style={{ transformOrigin: '50px 107px' }}>
          <circle cx={50} cy={107} r={15} />
          <path d="M50 93 V121 M36 107 H64" />
        </g>
        <g className="lto-front-reel" style={{ transformOrigin: '90px 107px' }}>
          <circle cx={90} cy={107} r={15} />
          <path d="M90 93 V121 M76 107 H104" />
        </g>
        {/* how far, and the lights */}
        <rect x={140} y={100} width={180} height={12} rx={6} className="lto-front-track" />
        <rect x={140} y={100} width={Math.max(6, 180 * Math.min(1, Math.max(0, progress)))} height={12} rx={6} className="lto-front-bar" />
        <text x={140} y={128} className="lto-front-read">
          {reading ? 'READ' : 'LOAD'} · {Math.round(progress * 100)}%
        </text>
        <circle cx={362} cy={60} r={5} className="lto-front-ready" />
        <circle cx={384} cy={60} r={5} className="lto-front-act" />
        <text x={352} y={80} className="lto-front-led">
          RDY ACT
        </text>
      </svg>
    </div>
  );
}

/** One LTO cartridge with its barcode label (and the generation's media code), as on the shelf. */
export function Cartridge({ barcode, generation, size = 'md' }: { barcode: string; generation?: string | null; size?: 'sm' | 'md' | 'lg' }) {
  return (
    <span className={`lto-cart ${size}`} style={{ '--shell': shellColour(generation) } as CSSProperties} aria-label={`${barcode} ${generation ?? ''}`}>
      <span className="lto-cart-notch" />
      <span className="lto-cart-label">
        <span className="lto-cart-bars" aria-hidden="true" />
        <span className="lto-cart-code">
          {barcode}
          <small>{mediaCode(generation)}</small>
        </span>
      </span>
    </span>
  );
}
